/**
 * WeeklyActivityStrip — the "This week" row on the parent dashboard Overview tab.
 *
 * Renders four honest, real weekly metrics derived ENTIRELY from the sessions list
 * already fetched for the Reports tab (useParentSessions → GET /parent/sessions).
 * No backend change and no fabricated data: every value is computed from session
 * records dated within the last 7 days.
 *
 *   1. Sessions this week   — count of (non-cancelled) sessions in the last 7 days
 *   2. Questions practised  — sum of questionCount across those sessions
 *   3. Days practised       — distinct calendar days with a session (out of 7)
 *   4. Learners active      — distinct children with a session (out of totalChildren)
 *
 * Redesign (2026-06): replaces the previous engagement chips (pebbles/streak/skill),
 * which were never populated by the backend (engagementSummary only carries
 * totalChildren / childrenWithActivity). See dashboard_service.get_parent_dashboard.
 *
 * TO-LL-036: pure presentational derivation — no async, no error swallowing.
 * TO-LL-044: reads exact camelCase keys (date, status, questionCount, childDisplayName).
 *
 * Modified TS-007 (T11, SR6, AC-TS007.6d): the weekly filter now ALSO excludes
 *   status === 'ended_by_parent' (mirroring the existing cancelled exclusion) so a
 *   parent-ended session never inflates sessionsThisWeek/questionsPractised/
 *   daysPractised/learnersActive.
 */

import type { ReactNode } from 'react';
import type { ParentSession } from '@/types/coins';

export interface WeeklyActivityStripProps {
  /** All parent-scoped sessions (most-recent first) from useParentSessions. */
  sessions: ParentSession[];
  /** Total number of children linked to the parent (for "Learners active X of N"). */
  totalChildren: number;
}

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

/** Compute the four weekly metrics from the sessions list (last 7 rolling days). */
function computeWeekly(sessions: ParentSession[]) {
  const now = Date.now();

  const week = sessions.filter((s) => {
    // TS-007 (T11, AC-TS007.6d): ended_by_parent excluded alongside cancelled — a
    // parent-ended session never enters the weekly aggregate.
    if (s.status === 'cancelled' || s.status === 'ended_by_parent') return false;
    if (!s.date) return false;
    const t = new Date(s.date).getTime();
    if (isNaN(t)) return false;
    const delta = now - t;
    return delta >= 0 && delta <= SEVEN_DAYS_MS;
  });

  const questionsPractised = week.reduce((sum, s) => sum + (s.questionCount ?? 0), 0);

  const days = new Set<string>();
  const learners = new Set<string>();
  for (const s of week) {
    days.add(new Date(s.date as string).toISOString().slice(0, 10));
    if (s.childDisplayName) learners.add(s.childDisplayName);
  }

  return {
    sessionsThisWeek: week.length,
    questionsPractised,
    daysPractised: days.size,
    learnersActive: learners.size,
  };
}

interface MetricProps {
  icon: string;
  iconBg: string;
  value: ReactNode;
  caption: string;
}

function Metric({ icon, iconBg, value, caption }: MetricProps) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-otter-sand bg-white px-4 py-3.5 shadow-sm">
      <span
        aria-hidden="true"
        className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl text-lg ${iconBg}`}
      >
        {icon}
      </span>
      <div className="min-w-0">
        <div className="font-display text-xl font-bold leading-none text-otter-earth-800">
          {value}
        </div>
        <div className="mt-1 text-[11.5px] font-semibold text-gray-500">{caption}</div>
      </div>
    </div>
  );
}

export function WeeklyActivityStrip({ sessions, totalChildren }: WeeklyActivityStripProps) {
  const { sessionsThisWeek, questionsPractised, daysPractised, learnersActive } =
    computeWeekly(sessions);

  const suffix = (text: string) => (
    <span className="font-body text-[13px] font-bold text-gray-400">{text}</span>
  );

  return (
    <div className="mb-6">
      <p className="mb-3 flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-wider text-pebbles-400">
        <span aria-hidden="true">✨</span> This week
      </p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Metric
          icon="📚"
          iconBg="bg-tutor-50"
          value={sessionsThisWeek}
          caption="Sessions this week"
        />
        <Metric
          icon="✏️"
          iconBg="bg-student-50"
          value={questionsPractised}
          caption="Questions practised"
        />
        <Metric
          icon="📅"
          iconBg="bg-amber-50"
          value={
            <>
              {daysPractised} {suffix('of 7 days')}
            </>
          }
          caption="Days practised"
        />
        <Metric
          icon="👧"
          iconBg="bg-[#e9f6f3]"
          value={
            <>
              {learnersActive} {suffix(`of ${totalChildren}`)}
            </>
          }
          caption="Learners active"
        />
      </div>
    </div>
  );
}
